import React, { useState, useEffect, useRef, useMemo } from "react";
import { Cloud } from "lucide-react";

// Color spectrum from calm to intense
const getWordIntensityColor = (normalizedValue) => {
  const colors = [
    { pos: 0, r: 100, g: 116, b: 139 },
    { pos: 0.25, r: 56, g: 142, b: 142 },
    { pos: 0.5, r: 180, g: 140, b: 80 },
    { pos: 0.75, r: 234, g: 88, b: 12 },
    { pos: 1, r: 220, g: 38, b: 38 }
  ];
  
  let lower = colors[0];
  let upper = colors[colors.length - 1];
  
  for (let i = 0; i < colors.length - 1; i++) {
    if (normalizedValue >= colors[i].pos && normalizedValue <= colors[i + 1].pos) {
      lower = colors[i];
      upper = colors[i + 1];
      break;
    }
  }
  
  const range = upper.pos - lower.pos;
  const t = range === 0 ? 0 : (normalizedValue - lower.pos) / range;
  
  const r = Math.round(lower.r + (upper.r - lower.r) * t);
  const g = Math.round(lower.g + (upper.g - lower.g) * t);
  const b = Math.round(lower.b + (upper.b - lower.b) * t);
  
  return `rgb(${r}, ${g}, ${b})`;
};

// Calculate font size based on influence (normalized value)
const getWordSize = (normalizedValue) => {
  // Min size: 11px, Max size: 22px
  const minSize = 11;
  const maxSize = 22;
  return Math.round(minSize + (maxSize - minSize) * normalizedValue);
};

export const WordCloudPanel = ({ wordCloudData, moodColor, onTopicClick, loaded = false }) => {
  const [displayData, setDisplayData] = useState(null);
  const hasLoadedOnce = useRef(false);

  // Update display data only when we have new valid data
  useEffect(() => {
    if (wordCloudData?.words?.length) {
      setDisplayData(wordCloudData);
      hasLoadedOnce.current = true;
    }
  }, [wordCloudData]);

  const { words, maxValue, minValue } = useMemo(() => {
    if (!displayData?.words?.length) return { words: [], maxValue: 1, minValue: 0 };
    const max = Math.max(...displayData.words.map(w => w.value));
    const min = Math.min(...displayData.words.map(w => w.value));
    return { words: displayData.words, maxValue: max, minValue: min };
  }, [displayData]);

  if (!words.length) {
    // Still waiting on the very first data response → show a loading hint.
    if (!hasLoadedOnce.current && !loaded) {
      return (
        <div className="empty-state">
          <Cloud size={20} style={{ opacity: 0.5 }} />
          <span>Loading topics...</span>
        </div>
      );
    }
    // App has loaded but this feed is genuinely empty → calm empty state
    // instead of an indefinite "Loading…" that reads as broken.
    return (
      <div className="empty-state">
        <Cloud size={20} style={{ opacity: 0.4 }} />
        <span>No trending topics right now</span>
      </div>
    );
  }
  
  // Limit to 12 words max to fit within bounds
  const displayWords = words.slice(0, 12);
  
  // Use rank-based normalization so words spread across the full color spectrum
  // even when values are close together
  const sortedByValue = [...displayWords].sort((a, b) => b.value - a.value);
  const rankMap = {};
  sortedByValue.forEach((w, i) => {
    rankMap[w.text] = displayWords.length > 1 ? i / (displayWords.length - 1) : 0.5;
  });
  
  return (
    <div className="word-cloud-container" data-testid="word-cloud">
      <div className="word-cloud-grid">
        {displayWords.map((word) => {
          const rankNorm = rankMap[word.text]; // 0 = highest rank (most frequent), 1 = lowest
          const wordColor = getWordIntensityColor(1 - rankNorm); // invert so highest = most intense
          const fontSize = getWordSize(1 - rankNorm);
          
          return (
            <span
              key={word.text}
              className="word-cloud-item clickable-topic"
              style={{ 
                color: wordColor,
                fontSize: `${fontSize}px`,
                fontWeight: rankNorm < 0.3 ? 600 : rankNorm < 0.6 ? 500 : 400,
                cursor: 'pointer'
              }}
              title={`Click to see articles about "${word.text}"`}
              onClick={() => onTopicClick && onTopicClick(word.text)}
              data-testid={`topic-${word.text.replace(/\s+/g, '-')}`}
            >
              {word.text}
            </span>
          );
        })}
      </div>
      
      <div className="word-cloud-legend">
        <span>Calm</span>
        <div className="legend-bar" />
        <span>Intense</span>
      </div>
    </div>
  );
};
